import { AlertColor } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege, languages } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditIndustryTypeForm } from './AddEditIndustryType/AddEditIndustryTypeForm';

const IndustryTypeList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];
  const userPrivileges = useSelector(Privilege) as string[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    return (languageList || []).map((language, indexLang) => {
      const field = { ...AddEditIndustryTypeForm(strings)[0] };
      field.field = `name_${language.value}`;
      field.autoFocus = indexLang === 0;
      field.label = `${field.label} (${language.text})`;
      field.responsive = { xs: 12 };
      return field;
    });
  }, [languageList]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.industryType, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.get_edit_delete_industryTypeById(recordId));
          const container: FormDataModel = {};
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
          });
          setFormValues(container);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = {};
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
        });
        setFormValues(container);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
      }));
      const payload = { localizedAttributes };
      if (editingId) {
        await axiosInstance.put(URLS.get_edit_delete_industryTypeById(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addIndustryType, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.get_edit_delete_industryTypeById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.industryType_title_list,
      subtitle: strings.industryType_list_subtitle,
      searchField: 'name',
      searchPlaceholder: 'Search industry types',
      AddButtonText: strings.industryType_button_add,
      listApi: URLS.industryType,
      deleteApi: URLS.get_edit_delete_industryTypeById,
      addPrivilege: privileges.add_industry_type,
      editPrivilege: privileges.edit_industry_type,
      deletePrivilege: privileges.delete_industry_type,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'name',
          headerName: strings.industryType,
          flex: 5,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        usageTableColumn(row => [
          { count: row.companyCount, singular: 'Company', plural: 'Companies' },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.industryType_button_edit : strings.industryType_button_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_industry_type)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default IndustryTypeList;
